import type { ReactNode } from 'react';
import { Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { useI18n } from '../i18n/I18nProvider';
import { Button, type ButtonVariant } from './Button';
import { cx } from './cx';
import styles from './Feedback.module.css';

export interface ModalDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children: ReactNode;
  /** alertdialog for confirmations that interrupt the user. */
  role?: 'dialog' | 'alertdialog';
  isDismissable?: boolean;
  wide?: boolean;
}

/** Modal dialog (focus trapped, Escape to close, focus restored on close). */
export function ModalDialog({
  isOpen,
  onOpenChange,
  title,
  children,
  role = 'dialog',
  isDismissable = true,
  wide,
}: ModalDialogProps) {
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      className={styles.overlay}
    >
      <Modal className={cx(styles.modal, wide && styles.modalWide)}>
        <Dialog role={role} className={styles.dialog}>
          <Heading slot="title" className={styles.dialogTitle}>
            {title}
          </Heading>
          {children}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}

export interface ConfirmDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  confirmVariant?: ButtonVariant;
  isPending?: boolean;
}

export function ConfirmDialog({
  isOpen,
  onOpenChange,
  title,
  children,
  confirmLabel,
  cancelLabel,
  onConfirm,
  confirmVariant = 'primary',
  isPending,
}: ConfirmDialogProps) {
  const { t } = useI18n();
  return (
    <ModalDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={title}
      role="alertdialog"
      isDismissable={!isPending}
    >
      <div className={styles.dialogBody}>{children}</div>
      <div className={styles.dialogActions}>
        <Button
          variant="secondary"
          onPress={() => onOpenChange(false)}
          isDisabled={isPending ?? false}
        >
          {cancelLabel ?? t.common.notNow}
        </Button>
        <Button
          variant={confirmVariant}
          onPress={onConfirm}
          isPending={isPending ?? false}
          autoFocus
          testId="confirm-action"
        >
          {confirmLabel}
        </Button>
      </div>
    </ModalDialog>
  );
}
